import React from "react"
import { MapSearch } from "./MapSearch"
import { EmptyView } from "@ciq/shared/components/views"
import { CollapsableContainer, CollapsableListView } from "@ciq/shared/components/collapsable"
import { MultiEntrySelector } from "@ciq/shared/components/modal"
import { useLeftPaneData } from "./useLeftPaneData"
import { useAnalyticsContext } from "@ciq/shared/contexts/AnalyticsContext"
import { ReactComponent as BuildingIcon } from "@ciq/shared/assets/icons/map/building_icon.svg"
import { ReactComponent as CampusIcon } from "@ciq/shared/assets/icons/map/campus_icon.svg"
import { ReactComponent as DefaultIcon } from "@ciq/shared/assets/icons/map/default_icon.svg"
import { ReactComponent as FloorIcon } from "@ciq/shared/assets/icons/map/floor_icon.svg"
import { ZoneType } from "@ciq/shared/utils/const"

export const getZoneIcon = (zone, onClick) => {
  const color = zone.iconColor

  switch (zone.type) {
    case ZoneType.CAMPUS:
      return <CampusIcon style={{ color }} onClick={onClick} />

    case ZoneType.BUILDING:
      return <BuildingIcon style={{ color }} onClick={onClick} />

    case ZoneType.FLOOR:
      return <FloorIcon style={{ color }} onClick={onClick} />

    default:
      return <DefaultIcon style={{ color }} onClick={onClick} />
  }
}

export const buildResourceData = (resource, listData) => ({
  resource,
  hasIcon: () => {
    return true
  },
  isSelected: () => {
    return resource.id === listData.resourceId
  },
  isSelectable: () => {
    return true
  },
  isOpen: () => {
    return listData.isOpen(resource.id)
  },
  isVisible: () => {
    return listData.isVisible(resource.id)
  },
  handleToggle: () => {
    return listData.toggleOpen(resource.id)
  },
  isCreatable: () => {
    return false
  },
  getIcon: (resource, onClick) => {
    return getZoneIcon(resource, onClick)
  },
  hasSubResources: () => {
    return resource.hasSubZones()
  },
  subResourceDatasources: () => {
    return resource.getSubZones().map(zone => buildResourceData(zone, listData))
  },
})

export const buildDatasource = listData => ({
  isSearching: listData.isSearching,
  numberOfRows: () => {
    return listData.resources?.length || 0
  },
  getDataForRow: row => {
    const resource = listData.resources[row]
    return buildResourceData(resource, listData)
  },
})

/**
 * Selected Inventory module for the left panel containing search, building/floor/usage filters, and resource list.
 * Collapsible section with:
 * - Search bar for filtering resources
 * - Buildings multi-select
 * - Floors multi-select
 * - Usage multi-select
 * - Hierarchical resource list view
 *
 * @returns {React.ReactElement} Selected Inventory module component
 */
export function SelectedInventoryModule() {
  const { search, listData, filters } = useLeftPaneData()
  const { map } = useAnalyticsContext()

  // Wrap filter onChange handlers with analytics tracking
  const buildingsWithTracking = {
    ...filters.buildings,
    onChange: values => {
      filters.buildings.onChange(values)
      map.trackFilterApplied({
        filter_type: "buildings",
        values: values.map(v => v.value || v),
      })
    },
  }

  const floorsWithTracking = {
    ...filters.floors,
    onChange: values => {
      filters.floors.onChange(values)
      map.trackFilterApplied({
        filter_type: "floors",
        values: values.map(v => v.value || v),
      })
    },
  }

  const usagesWithTracking = {
    ...filters.usages,
    onChange: values => {
      filters.usages.onChange(values)
      map.trackFilterApplied({
        filter_type: "usages",
        values: values.map(v => v.value || v),
      })
    },
  }

  // Wrap search with analytics tracking
  const searchWithTracking = {
    ...search,
    onSearch: query => {
      search.onSearch(query)
      map.trackSearchPerformed({
        query: query || "",
      })
    },
  }

  const content = () => {
    if (!listData.isLoading && !listData.hasData) {
      if (listData.isSearching) {
        return <EmptyView>No Resources match your search</EmptyView>
      }

      return <EmptyView>No Resources</EmptyView>
    }

    return (
      <CollapsableListView
        isLoading={listData.isLoading}
        scrollable={false}
        hasData={listData.hasData}
        datasource={buildDatasource(listData)}
        delegate={{
          onSelect: listData.set,
        }}
      />
    )
  }

  return (
    <div className="map-pane-inventory">
      <CollapsableContainer defaultOpenState={true} header="Selected Inventory">
        <MapSearch {...searchWithTracking} />
        <MultiEntrySelector {...buildingsWithTracking} />
        <MultiEntrySelector {...floorsWithTracking} />
        <MultiEntrySelector {...usagesWithTracking} />
        {content()}
      </CollapsableContainer>
    </div>
  )
}
